import type { ReactNode } from 'react'

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ')

export { cx }

/* ---------------------------------- Card ---------------------------------- */

export function Card({
  title, subtitle, action, children, className, padded = true,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  children?: ReactNode
  className?: string
  padded?: boolean
}) {
  return (
    <section
      className={cx(
        'rounded-lg border border-mc-line bg-mc-surface shadow-[0_1px_2px_rgba(18,22,28,.05)]',
        className,
      )}
    >
      {(title || action) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-mc-line px-4 py-3 sm:px-5">
          <div className="min-w-0">
            {title && (
              <h2 className="truncate text-sm font-bold tracking-wide text-mc-primary uppercase">
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-0.5 text-xs text-mc-muted">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      <div className={padded ? 'px-4 py-4 sm:px-5' : undefined}>{children}</div>
    </section>
  )
}

/* --------------------------------- Badge ---------------------------------- */

const BADGE_TONES = {
  neutral:  'bg-mc-surface-2 text-mc-body ring-mc-line',
  primary:  'bg-mc-medium-soft text-mc-primary ring-mc-primary/20',
  critical: 'bg-mc-critical-soft text-mc-critical ring-mc-critical/20',
  high:     'bg-mc-high-soft text-mc-high ring-mc-high/20',
  done:     'bg-mc-done-soft text-mc-done ring-mc-done/20',
  accent:   'bg-mc-accent-soft text-mc-high ring-mc-accent/30',
} as const

export type BadgeTone = keyof typeof BADGE_TONES

export function Badge({
  children, tone = 'neutral', className,
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export const toneForPriority = (p: string): BadgeTone =>
  p === 'Critical' ? 'critical' : p === 'High' ? 'high' : 'primary'

export const toneForStatus = (s: string): BadgeTone =>
  s === 'Decided' ? 'done' : s === 'Open' ? 'high' : 'neutral'

/* -------------------------------- Button ---------------------------------- */

const BUTTON_VARIANTS = {
  primary:
    'bg-mc-primary text-white hover:bg-mc-primary-dark disabled:bg-mc-primary/40',
  secondary:
    'bg-mc-surface text-mc-primary ring-1 ring-inset ring-mc-line hover:bg-mc-surface-2 disabled:text-mc-muted',
  ghost:
    'bg-transparent text-mc-primary hover:bg-mc-surface-2 disabled:text-mc-muted',
  danger:
    'bg-mc-critical text-white hover:brightness-110 disabled:bg-mc-critical/40',
} as const

// `ComponentPropsWithRef` rather than the attributes alone, so a caller can hold a ref to
// the button — the confirm dialog focuses one of its two. React 19 passes `ref` as an
// ordinary prop, so no forwarding is needed.
export function Button({
  variant = 'secondary', className, type = 'button', ...rest
}: React.ComponentPropsWithRef<'button'> & {
  variant?: keyof typeof BUTTON_VARIANTS
}) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded px-3 py-2 text-sm font-medium',
        'transition-colors disabled:cursor-not-allowed min-h-[36px]',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  )
}

/* -------------------------------- StatTile -------------------------------- */

export function StatTile({
  label, value, unit, hint, tone = 'neutral',
}: {
  label: string
  value: ReactNode
  unit?: string
  hint?: ReactNode
  tone?: BadgeTone
}) {
  const accent =
    tone === 'critical' ? 'text-mc-critical'
    : tone === 'high'   ? 'text-mc-high'
    : tone === 'done'   ? 'text-mc-done'
    : 'text-mc-primary'

  return (
    <div className="rounded-lg border border-mc-line bg-mc-surface px-4 py-3">
      <div className="text-xs font-medium tracking-wide text-mc-muted uppercase">{label}</div>
      <div className={cx('mt-1 flex items-baseline gap-1 font-bold tabular-nums', accent)}>
        <span className="text-2xl sm:text-3xl">{value}</span>
        {unit && <span className="text-sm font-medium text-mc-muted">{unit}</span>}
      </div>
      {hint && <div className="mt-1 text-xs text-mc-muted">{hint}</div>}
    </div>
  )
}

/* -------------------------------- Progress -------------------------------- */

export function ProgressBar({
  value, max, label, tone = 'primary',
}: {
  value: number
  max: number
  label?: string
  tone?: 'primary' | 'accent'
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div>
      {label && (
        <div className="mb-1 flex justify-between text-xs text-mc-muted">
          <span>{label}</span>
          <span className="tabular-nums">{pct.toFixed(1)}%</span>
        </div>
      )}
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-mc-surface-2"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progress'}
      >
        <div
          className={cx('h-full rounded-full', tone === 'accent' ? 'bg-mc-accent' : 'bg-mc-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

/* ------------------------------ Empty / Spinner --------------------------- */

export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="text-sm font-medium text-mc-body">{title}</p>
      {hint && <p className="mt-1 text-xs text-mc-muted">{hint}</p>}
    </div>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-mc-muted">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-mc-line border-t-mc-primary"
        aria-hidden
      />
      {label}
    </div>
  )
}

/* -------------------------------- Checkbox -------------------------------- */

/**
 * A checkbox and its label as one target.
 *
 * A bare input renders at 13px, which is below any usable touch target and well under the
 * 24px WCAG 2.5.8 minimum. The label is what gets tapped, so the whole row is sized rather
 * than the box: 44px on touch, tightened on pointer devices where it would otherwise look
 * oddly spaced.
 */
export function Checkbox({
  checked, onChange, label, disabled, className,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: React.ReactNode
  disabled?: boolean
  className?: string
}) {
  return (
    <label
      className={cx(
        'flex min-h-[44px] cursor-pointer items-center gap-2.5 text-sm text-mc-body select-none',
        'sm:min-h-[32px]',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-[18px] w-[18px] shrink-0 accent-[#355D7C]"
      />
      {label}
    </label>
  )
}
